export const normalizeLocaleString = (value: string | number) => {
  if (typeof value === 'number') {
    return value;
  }
  return value.replace(/\u2212/g, '-').replace(/\u00a0/g, ' ');
};

export const splitWithHyphen = (
  str: string | { href: string; name: string },
) => {
  if (typeof str !== 'string') {
    return str;
  }
  return (
    str?.slice(0, str?.length / 2) + '\u00AD' + str?.slice(str?.length / 2)
  );
};

export const toFixed = (
  value: number,
  decimals: number = 2,
  string: boolean = true,
  force: boolean = false,
) => {
  if (isNaN(value)) {
    return string ? '0' : 0;
  }
  if (value < 1 && decimals < 3 && value > 0) {
    decimals = 2;
  }
  let ret: any =
    Math.round((value + 0.00001) * Math.pow(10, decimals)) /
    Math.pow(10, decimals);
  ret = ret % 1 !== 0 || force ? ret.toFixed(decimals) : ret;
  if (string) {
    const isAboveThousand = ret.toString().indexOf('.') > -1;
    if (isAboveThousand) {
      ret = ret.toLocaleString('sv-SE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    } else {
      ret = ret.toLocaleString('sv-SE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }
    ret = normalizeLocaleString(ret.toString());
    const parts = ret.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    ret = parts.join(',').replace(/\./g, ',');
    return ret as string;
  }

  return parseFloat(ret);
};

export const reverseString = (str: string) => {
  const splitString = str.split('');
  const reverseArray = splitString.reverse();
  const joinArray = reverseArray.join('');
  return joinArray;
};

export const formatUnit = (unitString: string) => {
  switch (unitString) {
    case 'MWH':
      return 'MWh';
    case 'KWH':
      return 'kWh';
    default:
      return unitString;
  }
};

export const kronorOrOre = (valueInKr: number, withSuffix: boolean = true) => {
  const ret =
    Math.abs(toFixed(valueInKr, 4, false) as number) < 1
      ? `${toFixed(valueInKr * 100, 2)}${withSuffix ? ' öre' : ''}`
      : `${toFixed(valueInKr, 2)}${withSuffix ? ' kr' : ''}`;

  return withSuffix ? ret : parseInt(ret);
};

export const addToWindowObject = (name: string, objectToAdd: any) => {
  if (window && !(window as any)[name]) {
    (window as any)[name] = objectToAdd;
  }
};

export const addBusinessDays = (date: Date, businessDaysToAdd: number) => {
  const dateClone = new Date(date.getTime());

  if (dateClone.getDay() === 6) dateClone.setDate(dateClone.getDate() + 1);
  for (let i = 0; i < businessDaysToAdd; i++) {
    dateClone.setDate(dateClone.getDate() + 1);
    if (dateClone.getDay() === 6) dateClone.setDate(dateClone.getDate() + 2);
  }
  return dateClone;
};

export const formatNumber = (
  number: number,
  decimals: number = 2,
  trailingZeros: boolean = false,
) => {
  if (!number) return number;
  const formattedNumber = number.toLocaleString('sv-SE', {
    minimumFractionDigits: trailingZeros ? decimals : 0,
    maximumFractionDigits: decimals,
  });
  return normalizeLocaleString(formattedNumber);
};

export const conditionalRound = (
  number: number,
  decimals: number = 2,
  conditionalLevel: number = 10,
  formatted: boolean = true,
) => {
  if (number > conditionalLevel || number < -conditionalLevel) {
    return formatted ? formatNumber(Math.round(number)) : Math.round(number);
  }
  return formatted ? formatNumber(number, decimals) : number?.toFixed(decimals);
};

export const localstorageIsEnabled = () => {
  try {
    localStorage.setItem('testLocalstorage', 'test');
    localStorage.removeItem('testLocalstorage');
    return true;
  } catch (evt) {
    console.error('Localstorage is disabled', evt);
    return false;
  }
};

export const isProductionEnvironment = () => {
  return window.location.href.indexOf('mitt.malarenergi') > -1;
};

export const capitalizeFirstLetter = (text: string | null) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const formatAddress = (address: string) => {
  return address
    ?.split(' ')
    .map((word) => {
      if (/\d+[A-Za-z]/.test(word)) {
        return word.toUpperCase();
      } else if (/^LGH/.test(word) || /^NB/.test(word)) {
        return word.toLowerCase();
      } else {
        return capitalizeFirstLetter(word);
      }
    })
    .join(' ');
};

export const copyToClipboard = (text?: string) => {
  navigator.clipboard.writeText(text || '');
};

export const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const formatOrgPnr = (orgpnr: string) => {
  if (!orgpnr) return orgpnr;
  return orgpnr.replace(/\D/g, '').replace(/(\d{6})(\d{4})/, '$1-$2');
};

export const formatName = (name: string) => {
  const trimmedName = name?.trim();
  if (!trimmedName) return '';

  const lettersOnly = trimmedName.replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ]/g, '');
  const isAllUpperCase =
    lettersOnly.length > 0 && lettersOnly === lettersOnly.toUpperCase();
  const isAllLowerCase =
    lettersOnly.length > 0 && lettersOnly === lettersOnly.toLowerCase();

  if (isAllUpperCase || isAllLowerCase) {
    return trimmedName
      .split(' ')
      .map((word) => capitalizeFirstLetter(word))
      .join(' ');
  }

  return trimmedName;
};
